import React, {
  ComponentProps,
  memo,
  useEffect,
  useMemo,
  useState
} from 'react'

import cn from 'clsx'
import Clock from 'react-clock'
import 'react-clock/dist/Clock.css'
import { WidgetSize } from 'shared/constants/widgets'
import { PersistedWidgetSpec } from 'shared/types/widgets'

import { WidgetComponent } from '../../types/widgets.types'
import { WidgetWrapper } from '../WidgetWrapper'
import { useClockWidget } from './ClockWidget.hook'
import styles from './ClockWidget.module.scss'
import { formatTimeInZone, getZonedDate, tzName } from './ClockWidget.utils'
import { LargeWidgetTimezones, SmallWidgetTimezones } from './components'

type LiveClockProps = {
  timeZone: string
  size: number
} & ComponentProps<typeof Clock>

const LiveClock = memo(({ timeZone, size, ...props }: LiveClockProps) => {
  const [now, setNow] = useState<Date>(new Date())

  useEffect(() => {
    const id = setInterval(() => {
      setNow(new Date())
    }, 1000)
    return () => clearInterval(id)
  }, [])

  const value = useMemo(() => getZonedDate(timeZone, now), [timeZone, now])

  return (
    <Clock
      className={cn(props.className, styles.clock)}
      value={value}
      size={size}
      {...props}
    />
  )
})

const isValidTimezone = (tz: string): boolean => Boolean(tz && tz !== '')

export const ClockWidget: WidgetComponent = ({ widget, ...widgetProps }) => {
  const { config, contextMenuOptions } = useClockWidget(
    widget as PersistedWidgetSpec
  )

  const filteredSecondaryTimezones = useMemo(
    () => config.secondaryTimezones.filter(isValidTimezone),
    [config.secondaryTimezones]
  )

  const isSmallWidget = widget.size === WidgetSize.SMALL
  const timezonesForSmall = useMemo(
    () => [config.primaryTimezone, ...filteredSecondaryTimezones],
    [config.primaryTimezone, filteredSecondaryTimezones]
  )

  return (
    <WidgetWrapper
      {...widgetProps}
      widget={widget}
      contextMenuOptions={contextMenuOptions}
    >
      <div
        className={cn(styles.clockWidget, {
          [styles.leftAlign]: !isSmallWidget
        })}
      >
        {isSmallWidget ? (
          <SmallWidgetTimezones
            timezones={timezonesForSmall}
            getZonedDate={getZonedDate}
            tzName={tzName}
            LiveClock={LiveClock}
          />
        ) : (
          <LargeWidgetTimezones
            primaryTimezone={config.primaryTimezone}
            secondaryTimezones={filteredSecondaryTimezones}
            tzName={tzName}
            formatTimeInZone={formatTimeInZone}
            LiveClock={LiveClock}
          />
        )}
      </div>
    </WidgetWrapper>
  )
}
